import { Controller, useForm } from 'react-hook-form';
import { t } from '@app/i18n';
import { Alert, Card, ErrorBanner, Field, FieldGrid, LoadingButton, TextInput } from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { useUpdateTenant } from './TenantSettings.api';
import {
  channelsDefaults,
  forUpdateChannels,
  type ChannelsFormValues,
  type TenantConfig,
} from './TenantSettingsModel';
import { useChannelsValidation } from './useFormValidation';
import s from './TenantSettings.module.css';

export interface HumanSubjectChannelsCardProps {
  tenant: TenantConfig;
}

/**
 * Card **Canais da pessoa** de T21 (FR-116) — US14, T426.
 *
 * URA · cliente (issuer, JWKS URL, mapeamento do `sub`) e SSO · funcionário
 * (issuer, mapeamento). Informação **local**, para documentação e condições de
 * política: salvar é `updateTenant` e não gera nenhuma chamada ao middleware.
 */
export function HumanSubjectChannelsCard({ tenant }: HumanSubjectChannelsCardProps) {
  const canSave = useCanOperar('updateTenant');
  const save = useUpdateTenant();

  return (
    <Card
      title={t('tenantSettings.channels.title')}
      subtitle={t('tenantSettings.channels.subtitle')}
    >
      {save.error ? (
        <ErrorBanner
          error={save.error}
          escrita
          title={t('tenantSettings.channels.error')}
          hideLink
        />
      ) : null}
      {save.isSuccess && !save.isPending ? (
        <Alert tone="info" title={t('tenantSettings.channels.saved')} />
      ) : null}

      <ChannelsForm
        key={`${tenant.version}:${tenant.updated_at ?? ''}`}
        tenant={tenant}
        editavel={canSave}
        pending={save.isPending}
        onSubmit={(valores) => save.mutate(forUpdateChannels(tenant, valores))}
      />
    </Card>
  );
}

interface ChannelsFormProps {
  tenant: TenantConfig;
  editavel: boolean;
  pending: boolean;
  onSubmit: (valores: ChannelsFormValues) => void;
}

function ChannelsForm({ tenant, editavel, pending, onSubmit }: ChannelsFormProps) {
  const { control, handleSubmit, formState } = useForm<ChannelsFormValues>({
    defaultValues: channelsDefaults(tenant),
    ...useChannelsValidation(),
  });

  const campo = (
    name: keyof ChannelsFormValues,
    label: string,
    tip: string,
    extra: { mono?: boolean; placeholder?: string; span?: 6 | 12 } = {},
  ) => (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field label={label} span={extra.span ?? 6} error={fieldState.error?.message} tip={tip}>
          {(props) => (
            <TextInput
              {...props}
              {...field}
              mono={extra.mono}
              placeholder={extra.placeholder}
              disabled={!editavel}
            />
          )}
        </Field>
      )}
    />
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      aria-label={t('tenantSettings.channels.form.label')}
    >
      <h4 className={s.grupo}>{t('tenantSettings.channels.group.ivr')}</h4>
      <FieldGrid>
        {campo(
          'uraIssuer',
          t('tenantSettings.channels.field.ivrIssuer'),
          t('tenantSettings.channels.field.ivrIssuer.tip'),
          { mono: true, placeholder: t('tenantSettings.channels.field.ivrIssuer.placeholder') },
        )}
        {campo(
          'uraJwksUrl',
          t('tenantSettings.channels.field.ivrJwks'),
          t('tenantSettings.channels.field.ivrJwks.tip'),
          { mono: true, placeholder: t('tenantSettings.channels.field.ivrJwks.placeholder') },
        )}
        {campo(
          'uraMapeamento',
          t('tenantSettings.channels.field.ivrMapping'),
          t('tenantSettings.channels.field.ivrMapping.tip'),
          { span: 12, placeholder: t('tenantSettings.channels.field.ivrMapping.placeholder') },
        )}
      </FieldGrid>

      <h4 className={s.grupo}>{t('tenantSettings.channels.group.sso')}</h4>
      <FieldGrid>
        {campo(
          'ssoIssuer',
          t('tenantSettings.channels.field.ssoIssuer'),
          t('tenantSettings.channels.field.ssoIssuer.tip'),
          { mono: true, placeholder: t('tenantSettings.channels.field.ssoIssuer.placeholder') },
        )}
        {campo(
          'ssoMapeamento',
          t('tenantSettings.channels.field.ssoMapping'),
          t('tenantSettings.channels.field.ssoMapping.tip'),
          { placeholder: t('tenantSettings.channels.field.ssoMapping.placeholder') },
        )}
      </FieldGrid>

      {editavel ? (
        <div className={s.acoes}>
          <LoadingButton type="submit" loading={pending} disabled={!formState.isValid}>
            {t('tenantSettings.channels.form.save')}
          </LoadingButton>
        </div>
      ) : null}
    </form>
  );
}
